"use client";

import React from "react";
import { stegaClean } from "@sanity/client/stega";

import type { PagePatrickAffiliateSchema } from "@/sanity/pages/PagePatrickAffiliateTemplate";
import { withAffiliateOfferDiscount } from "@/lib/affiliateDiscount";
import { withAffiliateOfferCopy } from "@/lib/affiliateOfferCopy";

import HeroSection from "../pageComponents/patrickAffiliate/HeroSection";
import VipDealSection from "../pageComponents/patrickAffiliate/VipDealSection";
import AboutSection from "../pageComponents/patrickAffiliate/AboutSection";
import EdgeSection from "../pageComponents/patrickAffiliate/EdgeSection";
import FooterSection from "../pageComponents/patrickAffiliate/FooterSection";
import ScrollingBanner from "../pageComponents/global/ScrollingBanner";
import StepsSection from "../pageComponents/patrickAffiliate/StepsSection";
import TradersSaySection from "../pageComponents/patrickAffiliate/TradersSaySection";
import ComparisonSection from "../pageComponents/patrickAffiliate/ComparisonSection";
import TestimonialsSection from "../pageComponents/patrickAffiliate/TestimonialsSection";
import FAQSection from "../pageComponents/patrickAffiliate/FAQSection";
import FinalCTASection from "../pageComponents/patrickAffiliate/FinalCta";
import NewPricingSection from "../pageComponents/newPricing/NewPricingSection";
import PricingHeader from "../pageComponents/patrickAffiliate/PricingHeader";
import TradingCompetitionPopup from "@/components/global/TradingCompetitionPopup";

export default function PagePatrickAffiliateJSX({ data: rawData }: { data: PagePatrickAffiliateSchema }) {
  // This is a futures partner page, so the pricing section is pinned to
  // Futures unless the editor picks something else in "Pricing Section —
  // Asset": "Both" brings the Futures / CFD switcher back, "CFD + Crypto"
  // pins the other asset. Pinning hides the switcher inside the section.
  const pricingAssetKey = (stegaClean(rawData?.pricingSectionV2AssetKey) ||
    "futures") as NonNullable<PagePatrickAffiliateSchema["pricingSectionV2AssetKey"]>;
  const data = withAffiliateOfferCopy(
    withAffiliateOfferDiscount({ ...rawData, pricingSectionV2AssetKey: pricingAssetKey }),
    pricingAssetKey,
  );

  // Every checkout CTA in the pricing section has to carry the affiliate params,
  // and the VIP Deal button is the only CTA on this page that holds them.
  const affiliateCtaUrl = stegaClean(data?.vipDealCTA?.cta?.link?.url);

  return (
    <main className="bg-white">
      {/* Every size on this page is a rem, and the root font-size is a fraction
          of the viewport, so 1rem === 8px at the 1920 design width. Mirrors the
          rule in app/globals.scss — styled-jsx is the copy that wins at runtime. */}
      <style
        jsx
        global>{`
        :root {
          font-size: calc(700vw / 2200);
        }

        @media (max-width: 1920px) {
          :root {
            font-size: calc(700vw / 1920);
          }
        }

        @media (max-width: 992px) {
          :root {
            font-size: 50%;
          }
        }

        @media (max-width: 639px) {
          :root {
            font-size: calc(800vw / 435);
          }
        }

        /* A Blacker Display span inside a Geist heading would grow the line
           box by its own leading (5–8px per highlighted line); the heading's
           strut alone sets the line height, as in the design. */
        .richtext :is(h1, h2, h3, h4) span {
          line-height: 0;
        }
      `}</style>

      <TradingCompetitionPopup
        data={data?.tradingCompetitionPopup}
        language={data?.settings?.language}
        affCtaUrl={affiliateCtaUrl}
      />

      <HeroSection data={data} />

      <VipDealSection data={data} />

      <AboutSection data={data} />

      <EdgeSection data={data} />

      {data?.pricingSectionV3 && (
        <NewPricingSection
          sectionV3={data.pricingSectionV3}
          section={data.pricingSectionV2}
          language={data.settings?.language}
          fixedAsset={pricingAssetKey !== "both" ? pricingAssetKey : undefined}
          checkoutHrefOverride={affiliateCtaUrl}
          // The design's header for this page: Figma 8841:2386 / 8811:4021.
          renderHero={(copy) => (
            <PricingHeader
              data={data}
              copy={copy}
            />
          )}
          // 64 / 0 on mobile and 109 / 120 on desktop around the section — the
          // frame sits flush against the Edge CTA above and the pills below.
          sectionClassName="pt-[8rem] pb-0 lg:pt-[13.625rem] lg:pb-[15rem]"
          // 40px between the header and the asset tabs at both breakpoints.
          innerClassName="lg:gap-[5rem]"
        />
      )}

      {/* The pills strip repeats between pricing and the steps in the design —
          on desktop only; the mobile frame runs pricing straight into the steps. */}
      <ScrollingBanner
        className="hidden h-[8.625rem] border-y-0 border-t border-black/10 lg:flex"
        gapRem={6}
        separatorClassName="h-[0.5rem] w-[0.5rem] rounded-none"
        words={data?.scrollingWords?.words}
        wordsI18n={data?.scrollingWords?.wordsI18n}
      />

      <StepsSection data={data} />

      {(data?.tradersSayChip || data?.tradersSayHeading || data?.tradersSayItems?.length || data?.tradersSayCTA) && (
        <TradersSaySection data={data} />
      )}

      <ComparisonSection data={data} />

      {(data?.testimonialsChip ||
        data?.testimonialsHeading ||
        data?.testimonialsRating ||
        data?.testimonialsRatingText ||
        data?.testimonialsItems?.length) && <TestimonialsSection data={data} />}

      {(data?.faqChip || data?.faqHeading || data?.faqItems?.length) && <FAQSection data={data} />}

      {(data?.finalCtaChip ||
        data?.finalCtaHeading ||
        data?.finalCtaSubheading ||
        data?.finalCtaButton ||
        data?.finalCtaImage) && <FinalCTASection data={data} />}

      {data?.settings?.footer && (
        <FooterSection
          data={data.settings.footer}
          language={data?.settings?.language}
        />
      )}
    </main>
  );
}
